<template>
  <multiselect
    ref="multiselect"
    v-model="selectedOptions"
    :options="options"
    :multiple="!isSingle"
    :searchable="searchable"
    :preserve-search="true"
    :clear-on-select="false"
    :close-on-select="closeOnSelect"
    :custom-label="customLabel"
    :placeholder="placeholder"
    :max-height="maxHeight"
    v-bind="bindings"
    v-on="$listeners"
    @keydown.native="onKeydown"
  >
    <template #selection="props">
      <span
        class="multiselect__single"
        v-if="props.values.length && !props.isOpen"
      >
        <slot
          name="custom-selection"
          :values="props.values"
          :isOpen="props.isOpen"
        >
          <span v-if="props.values.length === 1">
            {{ formatSelection(props.values[0]) }}
          </span>
          <span v-else> {{ props.values.length }} options selected </span>
        </slot>
      </span>
    </template>

    <template #option="props">
      <slot name="option" v-bind="props">
        <span>{{ getOptionLabel(props.option) }}</span>
      </slot>
    </template>

    <template #tag>
      <span v-if="!isTagged"></span>
    </template>

    <template #clear="props" v-if="!noClearBtn">
      <div
        class="multiselect__clear"
        v-if="
          (isSingle && value) ||
          (!isSingle && selectedOptions && selectedOptions.length)
        "
        @mousedown.prevent.stop="clearAll(props.search)"
      ></div>
    </template>
    <template #noResult> No results found </template>
    <template #noOptions>{{ noOptionsText }}</template>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
  name: 'MultiSelectInput',
  components: {
    Multiselect,
  },
  props: {
    value: { type: [Array, Object, String], default: () => null },
    // When true, block the Enter key (and optionally other keys) until loading is false
    loading: { type: Boolean, default: false },
    // Optional array of key names (e.g. ['Enter', 'Escape']) to block while loading
    keysToBlock: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: '' },
    trackBy: { type: String, default: 'id' },
    customLabel: { type: Function },
    label: { type: String, default: '' },
    limit: { type: Number, default: 1 },
    selectLabel: { type: String, default: '' },
    deselectLabel: { type: String, default: '' },
    maxHeight: { type: Number, default: 300 },
    closeOnSelect: { type: Boolean },
    isTagged: { type: Boolean },
    isSingle: { type: Boolean },
    noClearBtn: { type: Boolean },
    noTracking: { type: Boolean },
    searchable: { type: Boolean, default: true },
    noOptionsText: { type: String, default: 'List is empty' },
  },
  data() {
    return {
      selectedOptions: this.value,
    };
  },
  computed: {
    bindings() {
      if (!this.noTracking) {
        return {
          ...this.$attrs,
          trackBy: this.trackBy,
          label: this.label,
        };
      }
      return this.$attrs;
    },
  },
  watch: {
    value: function (v) {
      this.selectedOptions = v;
    },
  },
  methods: {
    // Mirrors vue-multiselect's getOptionLabel + default customLabel so the
    // forwarded #option slot default renders identically to the unwrapped
    // component (incl. group headers via $groupLabel and tag options via isTag).
    getOptionLabel(option) {
      const isEmpty = (opt) => {
        if (opt === 0) return false;
        if (Array.isArray(opt) && opt.length === 0) return true;
        return !opt;
      };

      if (isEmpty(option)) return '';
      if (option.isTag) return option.label;
      if (option.$isLabel) return option.$groupLabel;

      const label = this.customLabel
        ? this.customLabel(option, this.label)
        : this.label
        ? option[this.label]
        : option;

      return isEmpty(label) ? '' : label;
    },
    formatSelection(option) {
      if (this.customLabel && typeof this.customLabel === 'function') {
        try {
          return this.customLabel(option);
        } catch (e) {
          // fall through to other formatting
        }
      }

      if (option && typeof option === 'object') {
        if (this.label && option[this.label] !== undefined)
          return option[this.label];
        if (option.name !== undefined) return option.name;
        if (option.label !== undefined) return option.label;
        return JSON.stringify(option);
      }

      return option;
    },
    clearAll() {
      this.selectedOptions = this.isSingle ? null : [];
      if (!this.closeOnSelect) this.$emit('input', this.selectedOptions);

      if (this.$refs.multiselect) {
        this.$refs.multiselect.updateSearch('');
      }
    },
    onKeydown(e) {
      // If loading is true, prevent Enter (or keys listed in keysToBlock)
      const key = e.key || (e.keyCode === 13 ? 'Enter' : e.keyCode);
      if (this.loading) {
        // If keysToBlock provided, check it; otherwise default to blocking Enter
        const shouldBlock =
          this.keysToBlock && this.keysToBlock.length
            ? this.keysToBlock.includes(key)
            : key === 'Enter';

        if (shouldBlock) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
      }
      // re-emit the keydown so parent listeners still work
      this.$emit('keydown', e);
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
.multiselect {
  &__select {
    height: 36px;
  }

  &__option--highlight {
    color: #35495e;
    background-color: transparent;
    &::after {
      display: none;
      background-color: #f3f3f3;
    }
    &:hover {
      background-color: #f3f3f3;
    }
    &.multiselect__option--selected {
      color: #35495e;
      background-color: transparent;
    }
  }

  &__option::after {
    top: 16px;
    right: 10px;
    padding: 0;
  }

  &__option--selected {
    background-color: transparent;
    span {
      display: inline-block;
      width: 96%;
      text-overflow: ellipsis;
      overflow: hidden;
    }
    &:hover {
      background-color: #f3f3f3;
    }
    &::before {
      height: 16px;
      width: 16px;
      border: 1px solid;
    }
    &::after {
      // display: none;
      content: '' !important;
      background: transparent !important;
      color: #35495e !important;
      display: inline-block;
      height: 6px;
      width: 9px;
      border-left: 2px solid;
      border-bottom: 2px solid;
      transform: rotate(-45deg);
    }
  }

  &.multiselect--disabled .multiselect__current,
  &.multiselect--disabled .multiselect__select {
    background: transparent;
  }

  &__tags {
    border: none;
    min-height: 36px;
    padding: 6px 40px 0 8px;
    border-radius: 4px !important;
  }
  &__tag {
    margin-bottom: 0;
  }
  &__content-wrapper {
    border: none;
    width: auto;
    min-width: 100%;
    // min-height: 200px;
    padding: 0;
    // top: 45px;
  }
  &__placeholder {
    margin-bottom: 4px;
  }
  &__input {
    border: none !important;
    background-color: transparent;
    &::placeholder {
      color: transparent;
    }
  }
  &__single {
    font-size: 13px;
    margin-top: 4px;
    background-color: transparent;
  }

  &__clear {
    position: absolute;
    right: 32px;
    height: 40px;
    width: 40px;
    margin-top: 1px;
    display: block;
    cursor: pointer;
    z-index: 2;
    &:before {
      transform: rotate(45deg);
    }
    &:after {
      transform: rotate(-45deg);
    }
    &:after,
    &:before {
      content: '';
      display: block;
      position: absolute;
      width: 3px;
      height: 16px;
      background: #aaa;
      top: 10px;
      right: 4px;
    }
  }
}
</style>
